import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Trophy, ArrowRight, SpinnerGap } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuccessPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('checking');
  const [club, setClub] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchClub();
  }, [slug]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, pollCount]);

  const fetchClub = async () => {
    try {
      const response = await axios.get(`${API}/clubs/${slug}`);
      setClub(response.data);
    } catch (err) {
      console.error('Error fetching club:', err);
    }
  };

  const pollPaymentStatus = async () => {
    if (pollCount >= 10) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => setPollCount(prev => prev + 1), 2000);
      }
    } catch (err) {
      console.error('Error checking payment:', err);
      setTimeout(() => setPollCount(prev => prev + 1), 2000);
    }
  };

  const clubPrimary = club?.primary_color || '#1D428A';

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: 'var(--bg-main)' }}
    >
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div className="animate-fade-in" data-testid="checking-payment">
            <div className="mb-6">
              <SpinnerGap size={80} weight="bold" className="mx-auto animate-spin" style={{ color: clubPrimary }} />
            </div>
            <h1 className="text-3xl mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Confirming Payment...
            </h1>
            <p className="text-gray-600">Please wait while we verify your payment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fade-in" data-testid="payment-success">
            <div className="mb-6 inline-block p-6 rounded-full" style={{ background: `${clubPrimary}20` }}>
              <CheckCircle size={80} weight="fill" style={{ color: clubPrimary }} />
            </div>
            <h1 className="text-4xl mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              You're In!
            </h1>
            <p className="text-gray-600 mb-8">
              Your entry has been confirmed. Good luck in the World Cup 2026 Predictor!
            </p>
            
            <div className="card p-6 mb-8 text-left">
              <h3 className="text-lg mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary }}>What's Next?</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-3">
                  <Trophy size={20} style={{ color: clubPrimary, flexShrink: 0, marginTop: 2 }} />
                  <span>Watch your teams progress through the World Cup</span>
                </li>
                <li className="flex items-start gap-3">
                  <Trophy size={20} style={{ color: clubPrimary, flexShrink: 0, marginTop: 2 }} />
                  <span>Earn points as your teams advance to each round</span>
                </li>
                <li className="flex items-start gap-3">
                  <Trophy size={20} style={{ color: clubPrimary, flexShrink: 0, marginTop: 2 }} />
                  <span>Check the predictor homepage when the tournament starts to see how your picks are doing</span>
                </li>
              </ul>
            </div>

            <div className="flex">
              <button
                onClick={() => navigate(`/worldcup2026/${slug}`)}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                style={{ background: clubPrimary, borderColor: clubPrimary }}
                data-testid="back-home-btn"
              >
                Back to Predictor <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {status === 'expired' && (
          <div className="animate-fade-in" data-testid="payment-expired">
            <h1 className="text-3xl mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: 'var(--error)' }}>
              Payment Expired
            </h1>
            <p className="text-gray-600 mb-8">
              Your payment session has expired. Please try again.
            </p>
            <button
              onClick={() => navigate(`/worldcup2026/${slug}`)}
              className="btn-primary"
              style={{ background: clubPrimary, borderColor: clubPrimary }}
              data-testid="try-again-btn"
            >
              Try Again
            </button>
          </div>
        )}

        {status === 'timeout' && (
          <div className="animate-fade-in" data-testid="payment-timeout">
            <h1 className="text-3xl mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Payment Processing
            </h1>
            <p className="text-gray-600 mb-8">
              We're still confirming your payment. Check your email for confirmation or try refreshing.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => { setStatus('checking'); setPollCount(0); }}
                className="btn-secondary"
              >
                Refresh Status
              </button>
              <button
                onClick={() => navigate(`/worldcup2026/${slug}`)}
                className="btn-primary"
                style={{ background: clubPrimary, borderColor: clubPrimary }}
              >
                Back to Predictor
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
